import * as React from 'react'
import { AutoCompleteInput, Button, Checkbox, FormField, IconButton, Input, IOption, Label, Modal, Select, ToggleFilter } from 'uxp/components';
import { IContextProvider } from '../../../uxp';
import classNames from 'classnames';
import Readonly from '../Readonly';
import { Conditional } from '../ConditionalComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hasProperty, hasValue, loadScriptFileAsync, tryParseJSON } from '../../../util';
import { WidgetDesignerPillInput } from "../pill-input/PillInput";
import { IWDDesignModeProps } from "../common";
import { IContextData } from '../../../hooks/useContextData';
import jp from 'jsonpath'
import _ from 'lodash'

export interface IRedirectUrl {
    url: string,
    target?: string
}

interface IWidgetProps {
    id: string,
    key: string,
    isTemplate: boolean,
    sourceUrl: string
    templateType?: string,
    templateKey?: string
    templateId?: string,
}

interface IAddOnProps {
    Usecase: string,
    Key: string,
    Name: string
    Version?: string,
    HasConfigured?: string
}
export interface IRedirectWidget {
    widget: IWidgetProps,
    params?: { [key: string]: any }
}
export interface IRedirectAddOn {
    addon: IAddOnProps,

}

export interface ILucyAction {
    modelName: string;
    action: string;
    disableParamters?: boolean,
    params: { [key: string]: any };
    dialogTitle?: string,
    executionMsg?: string,
    postExecution?: {
        success?: {
            msg?: string,
            action?: IAction,
            goToHome?: boolean,
            buttonTitle?: string,
            reloadWidget?: boolean,
            goBack?: number
        },
        error?: {
            msg?: string,
            action?: IAction,
            goToHome?: boolean,
            buttonTitle?: string,
            reloadWidget?: boolean,
            goBack?: number
        }
    }
}

export interface CustomEventProps {
    eventId: string,
    params?: { [key: string]: any }
}
export type IActionType = 'open-url' | 'open-widget' | 'execute-action' | 'open-addon' | 'open-mobile-ui' | 'trigger-custom-event'
export interface IAction {
    type: IActionType,
    redirectUrl?: IRedirectUrl
    redirectWidget?: IRedirectWidget;
    redirectAddOn?: IRedirectAddOn;
    lucyAction?: ILucyAction,
    mobileUI?: string,
    customEvent?: CustomEventProps
}

export interface IActionEditorLinkSettings {
    disableUrl?: boolean;
    disableWidget?: boolean;
    disableAction?: boolean;
    disableActionParameters?: boolean;
    disableAddOn?: boolean;
    disableMobileUI?: boolean
    disableTriggerEvent?: boolean
}

export interface IActionEditorLinkProps {
    action?: IAction;
    onChange: (action: IAction) => void;
    changeOnClose?: boolean,
    uxpContext: IContextProvider,
    contextData?: IContextData,
    settings?: IActionEditorLinkSettings,
    designer?: IWDDesignModeProps;
    uiProps: any;
}

export interface IActionStep {
    name: string,
    basePath: string,
    action: IAction,
    previousPath?: string
}

const ActionTypes = [
    { label: 'Open URL', value: 'open-url' },
    { label: 'Open Widget', value: 'open-widget' },
    { label: 'Execute Action', value: 'execute-action' },
    { label: 'Open Add-on', value: 'open-addon' },
    { label: 'Open Mobile Screen', value: 'open-mobile-ui' },
    { label: 'Trigger an Event', value: 'trigger-custom-event' },
];

export const DefaultAction: IAction = { type: 'open-url', redirectUrl: { url: 'about:blank' } }
const DefaultRedirectUrl: IRedirectUrl = { url: 'about:blank', target: '_blank' }
const DefaultRedirectAddon: IRedirectAddOn = { addon: null }
const DefaultRedirectWidget: IRedirectWidget = { widget: null, params: {} }
const DefaultLucyAction: ILucyAction = {
    modelName: '',
    action: '',
    params: {},
    dialogTitle: 'Execute Action',
    executionMsg: 'Executing action...',
    postExecution: {
        success: {
            msg: 'Executed successfully',
            goToHome: false,
            buttonTitle: 'Done',
            action: { ..._.cloneDeep(DefaultAction) },
            reloadWidget: false,
            goBack: 0
        },
        error: {
            msg: 'Execution failed',
            goToHome: false,
            buttonTitle: 'Close',
            action: { ..._.cloneDeep(DefaultAction) },
            reloadWidget: false,
            goBack: 0
        }
    }
}
const DefaultCustomEvent: CustomEventProps = {
    eventId: '',
    params: {}
}

export function extractAction(action: IAction, basePath?: string) {
    if (!action) return { ..._.cloneDeep(DefaultAction) }
    if (!basePath) basePath = '$'

    let { type, redirectUrl, redirectWidget, lucyAction, redirectAddOn } = action

    if (type == 'open-url') {
        action.redirectUrl = { ..._.cloneDeep(DefaultRedirectUrl), ...redirectUrl }
    }
    else if (type == 'open-widget') {
        action.redirectWidget = { ..._.cloneDeep(DefaultRedirectWidget), ...redirectWidget }
    }
    else if (type == 'execute-action') {
        // get success/error  action 
        lucyAction = { ..._.cloneDeep(DefaultLucyAction), ...lucyAction }
        let { success, error } = lucyAction.postExecution
        let successAction = extractAction(success.action, basePath + '.lucyAction.postExecution.success.action')
        let errorAction = extractAction(error.action, basePath + '.lucyAction.postExecution.error.action')

        lucyAction.postExecution.success.action = successAction
        lucyAction.postExecution.error.action = errorAction
        action.lucyAction = { ..._.cloneDeep(DefaultLucyAction), ...lucyAction }
    }
    else if (type == 'open-addon') {
        action.redirectAddOn = { ..._.cloneDeep(DefaultRedirectAddon), ...redirectAddOn }
    }

    return action
}

export function extractActionSteps(action: IAction, basePath?: string, steps?: IActionStep[], name?: string, previousPath?: string) {
    if (!basePath) basePath = "$"
    if (!steps) steps = []
    if (!name) name = 'Main Action'
    if (!previousPath) previousPath = null

    let { type, lucyAction } = action
    steps.push({ name, basePath, action, previousPath })

    if (type == 'execute-action') {
        if (lucyAction && lucyAction.postExecution) {
            let { success, error } = lucyAction.postExecution

            if (!!success && !success.goToHome && success.action) {
                let _steps = extractActionSteps(success.action, `${basePath}.lucyAction.postExecution.success.action`, steps, success.buttonTitle || 'Success', basePath)
                steps.concat(_steps)
            }

            if (!!error && !error.goToHome && error.action) {
                let _steps = extractActionSteps(error.action, `${basePath}.lucyAction.postExecution.error.action`, steps, error.buttonTitle || 'Error', basePath)
                steps.concat(_steps)
            }
        }
    }

    return steps
}

interface CustomEventType extends IOption {
    params?: { [key: string]: any }
}

const ActionEditorLink: React.FunctionComponent<IActionEditorLinkProps> = (props) => {
    let { disableAction, disableUrl, disableWidget, disableActionParameters, disableAddOn, disableMobileUI, disableTriggerEvent } = props.settings || {};

    let [isOpen, setIsOpen] = React.useState(false);
    let [action, setAction] = React.useState<IAction>({
        type: 'open-url',
        redirectUrl: { url: 'about:blank', target: "_blank" }
    })
    let [currentStepPath, setCurrentStepPath] = React.useState<string>('$')
    let [widgets, setWidgets] = React.useState<IWidgetProps[]>([]);
    let [installedAddOns, setInstalledAddOns] = React.useState<IAddOnProps[]>([]);
    const [events, setEvents] = React.useState<CustomEventType[]>([
        { label: 'Widget Overlay Status Event', value: 'dt-viewer-status-event', params: { isReady: false } },
        {
            label: 'Widget Overlay Change Event', value: 'dt-view-change-event', params: {
                objectId: '',
                objectType: '',
                layoutId: '',
            }
        },
    ])

    let hasMounted = React.useRef(false)
    let currentStepPathRef = React.useRef(null)

    React.useEffect(() => {
        hasMounted.current = true;
        return () => {
            hasMounted.current = false
        }
    }, [])

    React.useEffect(() => {
        if (hasMounted.current) {
            let act = props.action
            if (!act?.type || act.type.trim().length == 0) {
                act = { type: 'open-url', redirectUrl: { url: 'about:blank' } }
            }
            setAction(act)
        }
    }, [props.action])

    React.useEffect(() => {
        if (currentStepPathRef.current) {
            setCurrentStepPath(currentStepPathRef.current)
        }
        else if (!currentStepPath) {
            let steps = extractActionSteps(action)
            if (steps.length > 0) setCurrentStepPath(steps[0].basePath)
        }
    }, [action])

    React.useEffect(() => {
        getAllWidgets();
        getInstalledAddOns();
    }, [props.uxpContext]);

    function getName(widget: any) {
        if (hasValue(widget?.name)) return widget.name
        else if (hasValue(widget?.title)) return widget.title
        else if (hasValue(widget?.label)) return widget.label
        else return 'unknown'
    }
    function getAllWidgets() {
        props.uxpContext?.executeService('UXP', 'GetWidgets', { pageNumber: 0, max: 100000 }, { json: true })

            .then((res) => {
                let data = res[0]
                let widgets = tryParseJSON(data.widgets, [])

                // prepare widgets 
                let updatedWidgets: IWidgetProps[] = widgets.map((w: any) => {

                    let templateId = null
                    if (w.isTemplate && w.isDefaultTemplate) {
                        templateId = w.props.__template__.id
                    }

                    const name = getName(w)
                    console.log('name', name);

                    return {
                        name: name,
                        id: w.id,
                        sourceUrl: w.sourceUrl,
                        key: `${w.id}###${name.toLowerCase()}`,
                        isTemplate: w.isTemplate,
                        // templateKey: w.templateKey,
                        templateType: w.templateType,
                        templateId: templateId
                    } as IWidgetProps
                })
                console.log('updated widgets ', updatedWidgets);

                setWidgets(updatedWidgets);
            })
            .catch(e => {
                console.log('exception getting widgets', e);
            })
    }

    function getInstalledAddOns() {
        props.uxpContext?.executeService('Lucy', 'InstalledUseCase:InstalledUseCases', { max: 100, last: 0 }, { json: true }).then((data: IAddOnProps[]) => {
            setInstalledAddOns(data);


        }).catch((e) => {

        });
    }
    function getNameAndPath(action: IAction) {
        if (action.type == 'open-url') {
            return { name: 'Open URL', path: 'redirectUrl' }
        }
        if (action.type == 'open-widget') {
            return { name: 'Open Widget', path: 'redirectWidget' }
        }
        return { name: 'Execute Action', path: 'lucyAction' }
    }

    function renderBreadCrum() {
        let breadcrumbSteps: Array<IActionStep> = extractActionSteps(action)

        if (breadcrumbSteps.length < 2) return null
        return <div className="breadcrumb">
            {breadcrumbSteps.map((b, i) => {
                let active = b.basePath == currentStepPath
                return <div className={classNames("breadcrumb-step", { 'active': active })}
                    key={i}
                    onClick={() => { setCurrentStepPath(b.basePath) }}
                >
                    {b.name}
                </div>
            })}
        </div>
    }


    function renderToggle(step: IActionStep) {
        let _action: IAction = jp.value(action, step.basePath)

        let actionTypes = ActionTypes.filter((a) => {
            if (disableUrl && a.value == 'open-url') return false;
            if (disableWidget && a.value == 'open-widget') return false;
            if (disableAction && a.value == 'execute-action') return false;
            if (disableAddOn && a.value == 'open-addon') return false;
            if (disableMobileUI && a.value == 'open-mobile-ui') return false;
            if (disableTriggerEvent && a.value == 'trigger-custom-event') return false;
            return true
        })

        return <div className="action-type-toggle">

            {!!step.previousPath &&
                <div className="back-btn"
                    onClick={(e) => {
                        let parent = (e.currentTarget.parentNode).parentNode;
                        (parent as HTMLDivElement).classList.add('remove')

                        setTimeout(() => {
                            setCurrentStepPath(step.previousPath)
                        }, 200);
                    }}
                >
                    <FontAwesomeIcon icon={['fas', 'arrow-left']} />
                </div>
            }

            <ToggleFilter
                value={_action?.type || 'open-url'}
                options={actionTypes}
                onChange={(val) => {
                    jp.apply(action, `${step.basePath}.type`, () => (val))
                    props.onChange(action)
                }}
                className=""
            />
        </div>
    }

    function updateValue(_action: IAction, path: string, value: any, basePath: string) {
        jp.apply(_action, path, () => (value))
        if (basePath == '$') action = _action
        else jp.apply(action, basePath, () => (_action))
        props.onChange(action);
    }

    function renderUrlEditor(step: IActionStep) {
        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-url') return null

        // fill missing values 
        _action = { ..._action, redirectUrl: { ..._.cloneDeep(DefaultRedirectUrl), ..._action?.redirectUrl } }

        return <React.Fragment>

            <FormField>
                <Label className="lma-input">Url to redirect to</Label>
                <WidgetDesignerPillInput
                    value={_action?.redirectUrl?.url || ''}
                    onChange={(v) => { updateValue(_action, '$.redirectUrl.url', v, step.basePath) }}
                    className="lma-input"
                    placeholder="Ex: http://lucyinthesky.io/"
                    contextData={props.contextData}
                    showFields={true}
                />
            </FormField>
            <FormField>
                <Label className='lma-input'>Open in</Label>
                <Select
                    selected={_action?.redirectUrl?.target || '_blank'}
                    options={[
                        { label: 'New tab', value: '_blank' },
                        { label: 'Current tab', value: '_self' }
                    ]}
                    onChange={v => { updateValue(_action, '$.redirectUrl.target', v, step.basePath) }}
                    className="lma-input"
                />
            </FormField>
        </React.Fragment>
    }

    function renderExecuteAction(step: IActionStep) {

        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'execute-action') return null

        // fill missing values 
        if (!hasProperty(_action, '$.lucyAction')) {
            _action.lucyAction = { ..._.cloneDeep(DefaultLucyAction) }
        }
        _action = { ..._action, lucyAction: { ..._.cloneDeep(DefaultLucyAction), ..._action?.lucyAction } }
        if (!_action.lucyAction.postExecution.success.goBack) _action.lucyAction.postExecution.success.goBack = 0
        if (!_action.lucyAction.postExecution.error.goBack) _action.lucyAction.postExecution.error.goBack = 0

        return <div className='slider'>
            <div className='lucy-action-section'>
                <LucyActionEditor
                    uiProps={props.uiProps}
                    designer={props.designer}
                    value={_action.lucyAction}
                    onChange={(v) => { updateValue(_action, '$.lucyAction', v, step.basePath) }}
                    uxpContext={props.uxpContext}
                    contextData={props.contextData}
                    disableParameters={!!disableActionParameters}
                />
            </div>
            <div className='dialog-section'>
                <FormField>
                    <Label className="lma-input">Dialog Title</Label>
                    <Input
                        value={_action.lucyAction.dialogTitle || ''}
                        onChange={(v) => { updateValue(_action, '$.lucyAction.dialogTitle', v, step.basePath) }}
                        className="lma-input"
                        placeholder="Dialog title"
                    />
                </FormField>
                <FormField>
                    <Label className="lma-input">Execution Message</Label>
                    <Input
                        value={_action.lucyAction?.executionMsg || ''}
                        onChange={(v) => { updateValue(_action, '$.lucyAction.executionMsg', v, step.basePath) }}
                        className="lma-input"
                        placeholder="Execution Message"
                    />
                </FormField>

                <FormField>
                    <Label className="lma-input-section-label">On success</Label>

                    <FormField>
                        <Label className="lma-input">Completed Message</Label>
                        <Input
                            value={_action.lucyAction.postExecution.success.msg || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.msg', v, step.basePath) }}
                            className="lma-input"
                            placeholder="Completed Message"
                        />
                    </FormField>

                    <FormField>
                        <Label className="lma-input">Button Label</Label>
                        <Input
                            value={_action.lucyAction.postExecution.success.buttonTitle || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.buttonTitle', v, step.basePath) }}
                            className="lma-input"
                            placeholder="Done"
                        />
                    </FormField>

                    <Conditional visible={!(_action.lucyAction.postExecution.success?.goBack > 0) && !_action.lucyAction.postExecution.success.goToHome}>
                        <FormField>
                            <Checkbox
                                type='bordered'
                                checked={_action.lucyAction.postExecution.success.reloadWidget}
                                onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.reloadWidget', v, step.basePath) }}
                                className="lma-input"
                                label='Reload the widget on click the button'
                            />
                        </FormField>
                    </Conditional>

                    <Conditional visible={!(_action.lucyAction.postExecution.success?.goBack > 0) && !_action.lucyAction.postExecution.success.reloadWidget}>
                        <FormField>
                            <Checkbox
                                type='bordered'
                                checked={_action.lucyAction.postExecution.success.goToHome || false}
                                onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.goToHome', v, step.basePath) }}
                                label="Go To Home Page"
                                className="lma-input"

                            />
                        </FormField>
                    </Conditional>

                    <Conditional visible={!_action.lucyAction.postExecution.success.goToHome && !_action.lucyAction.postExecution.success.reloadWidget}>
                        <FormField>
                            <Label className="lma-input">Go back (number of screens)</Label>
                            <Input
                                type='number'
                                value={_action.lucyAction.postExecution.success.goBack?.toString() || '0'}
                                onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.goBack', Number(v), step.basePath) }}
                                className="lma-input"
                            />
                        </FormField>
                    </Conditional>

                    {/* <FormField>
                        <Checkbox
                            checked={_action.lucyAction.postExecution.success.goToHome || false}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.goToHome', v, step.basePath) }}
                            label="Go To Home Page"
                            className="lma-input"

                        />
                    </FormField>

                    <Conditional visible={!_action.lucyAction.postExecution.success.goToHome}>
                        <FormField>
                            <Label className="lma-input">Button Action</Label>
                            <Button title='Edit Action' onClick={() => {
                                setCurrentStepPath(step.basePath + '.lucyAction.postExecution.success.action')
                            }} />
                        </FormField>
                    </Conditional> */}

                </FormField>

                <FormField>
                    <Label className="lma-input-section-label">On error</Label>

                    <FormField>
                        <Label className="lma-input">Error Message</Label>
                        <Input
                            value={_action.lucyAction.postExecution.error.msg || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.msg', v, step.basePath) }}
                            className="lma-input"
                            placeholder="Error Message"
                        />
                    </FormField>

                    <FormField>
                        <Label className="lma-input">Button Label</Label>
                        <Input
                            value={_action.lucyAction.postExecution.error.buttonTitle || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.buttonTitle', v, step.basePath) }}
                            className="lma-input"
                            placeholder="Done"
                        />
                    </FormField>

                    <Conditional visible={!(_action.lucyAction.postExecution.error?.goBack > 0) && !_action.lucyAction.postExecution.error.goToHome}>
                        <FormField>
                            <Checkbox
                                type='bordered'
                                checked={_action.lucyAction.postExecution.error.reloadWidget}
                                onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.reloadWidget', v, step.basePath) }}
                                className="lma-input"
                                label='Reload the widget on click the button'
                            />
                        </FormField>
                    </Conditional>

                    <Conditional visible={!(_action.lucyAction.postExecution.error?.goBack > 0) && !_action.lucyAction.postExecution.error.reloadWidget}>
                        <FormField>
                            <Checkbox
                                type='bordered'
                                checked={_action.lucyAction.postExecution.error.goToHome || false}
                                onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.goToHome', v, step.basePath) }}
                                label="Go To Home Page"
                                className="lma-input"

                            />
                        </FormField>
                    </Conditional>

                    <Conditional visible={!_action.lucyAction.postExecution.error.goToHome && !_action.lucyAction.postExecution.error.reloadWidget}>
                        <FormField>
                            <Label className="lma-input">Go back (number of screens)</Label>
                            <Input
                                type='number'
                                value={_action.lucyAction.postExecution.error.goBack?.toString() || '0'}
                                onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.goBack', Number(v), step.basePath) }}
                                className="lma-input"
                            />
                        </FormField>
                    </Conditional>

                    {/* <FormField>
                        <Checkbox
                            checked={_action.lucyAction.postExecution.error.goToHome || false}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.goToHome', v, step.basePath) }}
                            label="Go To Home Page"
                            className="lma-input"

                        />
                    </FormField>

                    <Conditional visible={!_action.lucyAction.postExecution.error.goToHome}>
                        <FormField>
                            <Label className="lma-input">Button Action</Label>
                            <Button title='Edit Action' onClick={() => {

                                if (!hasProperty(_action, '$.lucyAction.postExecution.error.action')) {
                                    let _defaultAction: IAction = { type: 'open-url' }
                                    currentStepPathRef.current = step.basePath + '.lucyAction.postExecution.error.action'
                                    updateValue(_action, '$.lucyAction.postExecution.error.action', _defaultAction, step.basePath)
                                }
                                else {
                                    setCurrentStepPath(step.basePath + '.lucyAction.postExecution.error.action')
                                }
                            }} />
                        </FormField>
                    </Conditional> */}

                </FormField>

            </div>
        </div>
    }

    async function onSelectAWidget(widget: IWidgetProps) {
        return new Promise(async (done, nope) => {
            let registered = (window as any).Widgets || []
            let instance = registered.find((r: any) => r.id == widget.id)
            if (!instance) {
                await loadScriptFileAsync(widget.sourceUrl)
            }
            instance = registered.find((r: any) => r.id == widget.id)
            if (!instance) {
                console.log('Unable to find the widget even after loading the module');
                return done({})
            }

            // get the props 
            let props = instance?.configs?.props || []
            let defaultProps = instance?.defaultProps || {}

            let _props: { [key: string]: any } = {}
            props.map((p: any) => {
                if (p.name != 'uiProps') {
                    _props[p.name] = defaultProps[p.name] || ''
                }
            })
            done(_props)
        })
    }

    function renderPageEditor(step: IActionStep) {
        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-widget') return null

        if (!hasProperty(_action, '$.redirectWidget')) {
            _action.redirectWidget = { ..._.cloneDeep(DefaultRedirectWidget) }
        }
        _action = { ..._action, redirectWidget: { ..._.cloneDeep(DefaultRedirectWidget), ..._action?.redirectWidget } }

        const _widgets = [...widgets]
        if (!_widgets.find(w => w.key === _action.redirectWidget?.widget?.key)) {
            const newItem = {
                id: _action.redirectWidget?.widget?.key,
                name: _action.redirectWidget?.widget?.key,
                key: _action.redirectWidget?.widget?.key,
                isTemplate: false,
                sourceUrl: null,

            } as IWidgetProps
            _widgets.unshift(newItem)
        }

        return <React.Fragment>
            <FormField>
                <Label className="lma-input">Select a page</Label>
                <Select
                    selected={_action.redirectWidget?.widget?.key || ''}
                    options={widgets}
                    labelField="name"
                    valueField="key"
                    onChange={(v, o) => {

                        onSelectAWidget(o)
                            .then(props => {
                                let _redirectwidget: IRedirectWidget = {
                                    widget: o,
                                    params: props || {}
                                }
                                updateValue(_action, '$.redirectWidget', _redirectwidget, step.basePath)
                            })
                    }}
                    className="lma-input"
                    placeholder="Select a Widget"
                    addNewValues={{
                        enable: true,
                        title: 'Add Value',
                        loadingTitle: 'Adding...',
                        onAddNewValue: (value) => {
                            return new Promise<any>((done, nope) => {
                                const newItem = { name: value, id: value, key: value, isTemplate: false, sourceUrl: null } as IWidgetProps
                                setWidgets(prev => {
                                    prev.push(newItem)
                                    return prev
                                })
                                setTimeout(() => { done(newItem) }, 1000)
                            })
                        },
                    }}
                />
            </FormField>

            <FormField>
                <ActionParameterEditor
                    designer={props.designer}
                    uiProps={props.uiProps}
                    value={_action.redirectWidget.params || {}}
                    onChange={(v) => { updateValue(_action, '$.redirectWidget.params', v, step.basePath) }}
                    contextData={props.contextData}
                />
            </FormField>
        </React.Fragment>;
    }

    function renderAddOnEditor(step: IActionStep) {
        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-addon') return null

        if (!hasProperty(_action, '$.redirectAddOn')) {
            _action.redirectAddOn = { ..._.cloneDeep(DefaultRedirectAddon) }
        }
        _action = { ..._action, redirectAddOn: { ..._.cloneDeep(DefaultRedirectAddon), ..._action?.redirectAddOn } }

        return <React.Fragment>
            <FormField>
                <Label className="lma-input">Select a Add-on</Label>
                <Select
                    selected={_action.redirectAddOn?.addon?.Key || ''}
                    options={installedAddOns}
                    labelField="Name"
                    valueField="Key"
                    onChange={(v, o) => {


                        let _redirectAddOn: IRedirectAddOn = {
                            addon: o
                        }
                        updateValue(_action, '$.redirectAddOn', _redirectAddOn, step.basePath)
                    }}
                    className="lma-input"
                    placeholder="Select a Addon"
                />
            </FormField>


        </React.Fragment>;
    }

    function renderMobileUIEditor(step: IActionStep) {

        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-mobile-ui') return null

        if (!_action.hasOwnProperty('mobileUI')) _action.mobileUI = ''

        const options: { label: string, value: string }[] = [
            { value: 'settings', label: 'Settings' },
            { value: 'feeds', label: 'News Feeds' },
            { value: 'qrview', label: 'QR Code Scanner' },
            { value: 'current_items', label: 'Notifications' },
            { value: 'notifications', label: 'Notifications' },
            { value: 'currency_screen', label: 'Currency' },
            { value: 'language_screen', label: 'Language' },
        ]

        if (hasValue(_action?.mobileUI)) {
            const opt = options.find(op => op.value == _action?.mobileUI)
            if (!opt) {
                options.push({ label: _action.mobileUI, value: _action.mobileUI })
            }
        }

        return <React.Fragment>

            <Label>Note: This will only work in the mobile app</Label>

            <FormField>

                {/* <AutoCompleteInput
                    value={_action.mobileUI || ''}
                    onChange={s => {
                        updateValue(_action, '$.mobileUI', s, step.basePath)
                    }}
                    className='lma-input'
                    options={['Qr Code Scanner', 'Settings Page']}
                /> */}

                <Select
                    selected={_action.mobileUI || ''}
                    onChange={s => {
                        updateValue(_action, '$.mobileUI', s, step.basePath)
                    }}
                    className='lma-input'
                    options={options}
                    addNewValues={{
                        enable: true,
                        title: 'Use this value',
                        loadingTitle: 'Updating...',
                        onAddNewValue: async (value) => {
                            updateValue(_action, '$.mobileUI', value, step.basePath)
                            return true
                        }
                    }}
                />
            </FormField>
        </React.Fragment>
    }

    function getEventParams(eventId: string) {
        const eventType = events.find(e => e.value == eventId)
        return eventType ? eventType?.params || {} : {}
    }
    function renderTriggerCustomEvent(step: IActionStep) {

        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'trigger-custom-event') return null

        if (!hasProperty(_action, '$.customEvent')) {
            _action.customEvent = { ...DefaultCustomEvent }
        }

        if (!_action?.customEvent?.params) {
            _action.customEvent.params = getEventParams(_action?.customEvent?.eventId)
        }

        _action = Object.assign(DefaultCustomEvent, _action)

        return <React.Fragment>
            <FormField>
                <Label className="lma-input">Select an Event </Label>
                <Select
                    selected={_action?.customEvent?.eventId || ''}
                    options={events}
                    onChange={(v, o) => {

                        const event: CustomEventProps = {
                            eventId: v,
                            params: o?.params || {}
                        }
                        updateValue(_action, '$.customEvent',event, step.basePath)
                    }}
                    className="lma-input"
                    placeholder="Select an Event"
                    addNewValues={{
                        enable: true,
                        title: 'Add Event',
                        loadingTitle: 'Adding...',
                        onAddNewValue: (value) => {
                            return new Promise<any>((done, nope) => {
                                const newItem: IOption = { label: value, value: value }
                                setEvents(prev => {
                                    prev.push(newItem)
                                    return prev
                                })
                                setTimeout(() => { done(newItem) }, 1000)
                            })
                        },
                    }}
                />
            </FormField>

            <FormField>
                <ActionParameterEditor
                    designer={props.designer}
                    uiProps={props.uiProps}
                    value={_action.customEvent.params || {}}
                    onChange={(v) => { updateValue(_action, '$.customEvent.params', v, step.basePath) }}
                    contextData={props.contextData}
                />
            </FormField>
        </React.Fragment>;
    }

    function renderContent(s: IActionStep, i: number) {
        let currentIndex = extractActionSteps(action).findIndex(s => s.basePath == currentStepPath)

        // render only up to the current step
        if (i > currentIndex) return null

        return <div className={classNames('action-editor-step')}>
            {renderToggle(s)}
            {renderUrlEditor(s)}
            {renderExecuteAction(s)}
            {renderPageEditor(s)}
            {renderAddOnEditor(s)}
            {renderMobileUIEditor(s)}
            {renderTriggerCustomEvent(s)}
        </div>
    }

    let headerContent = <div className="hc">
        <Label>Action Editor</Label>

        {/* show done button only for the first item. others will have there own done buttons  */}
        {/* <Conditional visible={currentStepPath == "$"}> */}
        <div className="close-btn">
            <Button title="Done" onClick={() => {
                if (props.changeOnClose) props.onChange(action)
                if (hasMounted.current) {
                    setIsOpen(false)
                }
            }} />
        </div>
        {/* </Conditional> */}
    </div>
    return <React.Fragment>
        <Button title={'Edit Action'} onClick={() => setIsOpen(true)} className="active" />

        <Modal
            className={'mc-action-editor'}
            show={isOpen}
            onClose={() => { }}
            headerContent={headerContent}
            backgroundDismiss={false}
        >
            {/* {renderBreadCrum()} */}
            <div className="content">
                {/* <TransitionGroup
                    transitionName="example"
                    transitionEnterTimeout={500}
                    transitionLeaveTimeout={300}> */}
                {
                    extractActionSteps(action).map((s, i) => (renderContent(s, i)))
                }
                {/* </TransitionGroup> */}
            </div>

        </Modal>

    </React.Fragment>
}

const ActionParameterEditor: React.FunctionComponent<{ value: any, onChange: (v: any) => void, contextData?: IContextData, designer: IWDDesignModeProps, uiProps: any }> = (props) => {

    let [newKey, setNewKey] = React.useState('');
    let [newValue, setNewValue] = React.useState('');

    let params = props.value || {};
    let keys = Object.keys(params);
    function renderRow(key: string, value: any, i: number) {

        return <tr key={i}>
            <td>
                <Readonly value={key} className="lma-property-input" />
            </td>
            <td>
                <WidgetDesignerPillInput
                    value={value}
                    onChange={(val) => props.onChange(Object.assign({}, params, { [key]: val }))}
                    className="widget-designer-input"
                    contextData={props.contextData}
                    showFields={true}
                />
                {/* <Input
                    value={value}
                    onChange={(val) => props.onChange(Object.assign({}, params, { [key]: val }))}
                    className="lma-property-input"
                    placeholder="Value"
                /> */}
            </td>
            <td>

                <IconButton
                    type="delete"
                    size="small"
                    onClick={() => {
                        let x = Object.assign({}, params);
                        delete x[key];
                        props.onChange(x);
                    }}
                />
            </td>
        </tr>;
    }
    return <div className='axn-params'><table className='axn-param-table'>

        <thead>
            <tr>
                <th>Parameter Name</th>
                <th>Parameter Value</th>
                <th className='action-cell'></th>
            </tr>
        </thead>

        <tbody>
            {
                keys.map((k, i) => {
                    return renderRow(k, params[k], i);
                })
            }
            <tr key={'new'}>
                <td>
                    <Input
                        value={newKey}
                        onChange={(e) => setNewKey(e)}
                        className="lma-property-input"
                        placeholder="Key"
                    />
                </td>
                <td>
                    {/* <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e)}
                        className="lma-property-input"
                        placeholder="Value"
                    /> */}

                    <WidgetDesignerPillInput
                        value={newValue}
                        onChange={(e) => setNewValue(e)}
                        className="widget-designer-input"
                        contextData={props.contextData}
                        showFields={true}
                    />

                </td>
                <td>
                    <IconButton
                        type="plus"
                        size="small"
                        onClick={() => {
                            setNewKey('');
                            setNewValue('');
                            props.onChange(Object.assign({}, params, { [newKey]: newValue }));
                        }}
                    />
                </td>

            </tr>
        </tbody>
    </table>
    </div>;
}

const LucyActionEditor: React.FunctionComponent<{ value: ILucyAction, onChange: (v: ILucyAction) => void, uxpContext?: IContextProvider, contextData?: IContextData, designer?: IWDDesignModeProps, uiProps: any, disableParameters?: boolean }> = (props) => {
    let action: ILucyAction = props.value || { action: '', modelName: '', params: {} };

    let [models, setModels] = React.useState([])
    let [actions, setActions] = React.useState([])

    React.useEffect(() => {
        getAllModels()
    }, [])

    React.useEffect(() => {
        if (action.modelName) {
            getAllActions(action.modelName)
        }
    }, [action])

    async function getActionDetails(model: string, action: string) {
        return new Promise((done, nope) => {
            props?.uxpContext?.executeService('System', 'MetadataMap:ActionDetailsByName', { 'Model': model, 'Action': action }, { json: true })
                .then(res => {
                    let definition = tryParseJSON(res[0]?.Definition || {}, {})
                    let params: any = {}
                    if (definition.parameters) {
                        definition.parameters.map((p: string) => {
                            params[p] = ''
                        })
                    }
                    done(params)
                })
                .catch(e => {
                    console.log("unable to get the params ", e);
                    done({})
                })
        })
    }

    function getAllModels() {
        props?.uxpContext?.executeService('System', 'MetadataMap:ActiveModelNames', {}, { json: true })
            .then(res => {
                let models = res.flatMap((m: any) => m.ObjectID)
                setModels(models)
            })
            .catch(e => {
                console.log('excepton ', e);
            })
    }

    function getAllActions(model: string) {
        props?.uxpContext?.executeService("System", "MetadataMap:ActiveModelActions", { modelName: model }, { json: true })
            .then(res => {
                let actions = res.flatMap((a: any) => a.ObjectID)
                setActions(actions)
            })
            .catch(e => console.log(e))
    }


    return <div className='lucy-action-editor'>
        <FormField>
            <Label className="lma-input">Model</Label>
            <AutoCompleteInput
                value={action.modelName || ''}
                onChange={(txt) => {
                    let _action = { ...action }
                    _action.modelName = txt
                    if (txt.trim().length == 0) {
                        _action.action = ''
                        _action.params = {}
                    }
                    props.onChange(_action)
                }}
                options={models}
                className="lma-input"
                placeholder="Model name"
            />
        </FormField>
        <FormField>
            <Label className="lma-input">Action</Label>

            <AutoCompleteInput
                value={action.action || ''}
                onChange={(txt) => {

                    getActionDetails(action.modelName, txt)
                        .then(res => {
                            let _action = { ...action }
                            _action.action = txt
                            _action.params = res || {}
                            props.onChange(_action)
                        })
                }}
                options={actions}
                className="lma-input"
                placeholder="Action name"
            />
        </FormField>
        {
            !props?.disableParameters && <FormField>
                <ActionParameterEditor
                    designer={props.designer}
                    uiProps={props.uiProps}
                    value={action.params}
                    onChange={(v) => props.onChange({ ...action, params: v })}
                    contextData={props.contextData}
                />
            </FormField>
        }

    </div>;
};

export default ActionEditorLink